/***************************************************************************************************
 * Load `$localize` onto the global scope - used if i18n tags appear in Angular templates.
 */
import '@angular/localize/init';

import { enableProdMode, InjectionToken, APP_INITIALIZER, importProvidersFrom } from '@angular/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { BrowserAnimationsModule, provideAnimations } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';

import { environment } from './environments/environment';
import { AppComponent } from './app/app.component';
import { AppRouterModule } from './app/app.routes';
import { CoreModule } from '@core/core.module';
import { HomeModule } from '@modules/home/home.module';
import { Cf2MaterialModule } from '@modules/cf2-material/cf2-material.module';
import { TranslocoRootModule } from './app/transloco/transloco-root.module';
import { NativeDateModule } from '@angular/material/core';

import {
  MsalModule,
  MsalService,
  MsalGuard,
  MsalInterceptor,
  MsalBroadcastService,
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
} from '@azure/msal-angular';

import {
  MsalInstanceFactory,
  MsalInterceptorConfigFactory,
  initializeMsalInstance,
  ConsentScopes,
} from '@core/factories/cf2-msal.factory';

import { InteractionType } from '@azure/msal-browser';
import { provideNgxMask } from 'ngx-mask';
import {SharedModule} from "@shared/shared.module";
import {platformBrowserDynamic} from "@angular/platform-browser-dynamic";
import {AppModule} from "./app/app.module";

/* -------------------- ENV -------------------- */

if (environment.production) {
  enableProdMode();
}

/* -------------------- BASE URL -------------------- */

export function getBaseUrl(): string {
  return document.getElementsByTagName('base')[0].href;
}

export function getApiUrl(): string {
  return getBaseUrl() + 'api/';
}

export const cf2BaseUrl = window.location.href;
export type BaseUrlType = typeof cf2BaseUrl;

export const BASE_URL = new InjectionToken<BaseUrlType>('base url');

export let azureClientId = ''; 
export let azureTenantId = ''; 

export function setClientId(value: string) { 
  azureClientId = value; 
} 

export function setTenantId(value: string) { 
  azureTenantId = value; 
}

/* -------------------- THEME PRELOAD -------------------- */

try {
  const xmlHttp = new XMLHttpRequest();
  xmlHttp.open('GET', getBaseUrl() + 'api/ApplicationSettings/theme', false);
  xmlHttp.send(null);

  const newColours = JSON.parse(xmlHttp.responseText);
  document.documentElement.style.setProperty('--production-primary-color', newColours.primaryColour);
  document.documentElement.style.setProperty('--secondary-color', newColours.secondaryColour);
  document.documentElement.style.setProperty('--production-accent-color', newColours.accentColour);
  document.documentElement.style.setProperty('--alternating-row-color', newColours.alternatingRowColour);
  document.documentElement.style.setProperty('--panel-header-color', newColours.panelHeaderColour);
} catch {
  console.log('Error setting dynamic theme');
}

/* -------------------- MSAL -------------------- */

export function MSALGuardConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: { scopes: ConsentScopes },
    loginFailedRoute: '/login-failed',
  };
}

export function initializeApp() {
  return async () => {
    try {
      await initializeMsalInstance();
      console.log('MSAL initialized successfully');
    } catch (err) {
      console.error('Failed to initialize MSAL:', err);
    }
  };
}

/* -------------------- BOOTSTRAP -------------------- */

platformBrowserDynamic()
.bootstrapModule(AppModule)
.catch(err => console.error(err));
